/**
 * Top Bar Layout Template
 *
 * Provides consistent internal structure for top bars across different modes.
 * - Left section for primary controls (logo, dropdowns, etc.)
 * - Right section for actions (buttons, CTAs)
 * - Integrated three-dots menu (always present, items vary by mode)
 *
 * Use this with TopBarContainer for complete top bar implementation.
 */

import React, { useState } from 'react';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { TopBarContainer } from '../primitives/TopBarContainer';

export interface MenuItemConfig {
  /**
   * Display label for the menu item
   */
  label: string;

  /**
   * Optional icon to display before label
   */
  icon?: React.ReactNode;

  /**
   * Click handler
   */
  onClick: () => void;

  /**
   * Whether item is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Show divider before this item
   * @default false
   */
  divider?: boolean;

  /**
   * Optional keyboard shortcut to display (e.g., "⌘Z")
   */
  shortcut?: string;

  /**
   * Optional tooltip shown on hover (works for disabled items too),
   * e.g. explaining why the item is disabled
   */
  tooltip?: string;
}

export interface TopBarLayoutProps {
  /**
   * Left section content (logo, dropdowns, etc.)
   */
  left?: React.ReactNode;

  /**
   * Right section content (action buttons)
   */
  right?: React.ReactNode;

  /**
   * Menu items for three dots menu
   * Menu is always present, but hidden if no items provided
   */
  menuItems?: MenuItemConfig[];

  /**
   * Optional callback when menu is opened
   */
  onMenuOpen?: () => void;
}

/**
 * TopBarLayout - Structural template for top bars
 *
 * Handles internal layout with three sections: left, right, and menu.
 * Always renders inside TopBarContainer for positioning.
 *
 * @example
 * ```tsx
 * <TopBarLayout
 *   left={
 *     <>
 *       <TemplateDropdown {...} />
 *       <EditButton {...} />
 *     </>
 *   }
 *   right={
 *     <Button>Copy All</Button>
 *   }
 *   menuItems={[
 *     { label: 'Undo', icon: <UndoIcon />, onClick: handleUndo, shortcut: '⌘Z' },
 *     { label: 'Delete', icon: <DeleteIcon />, onClick: handleDelete, divider: true },
 *   ]}
 * />
 * ```
 */
export const TopBarLayout = React.forwardRef<HTMLDivElement, TopBarLayoutProps>(
  ({ left, right, menuItems = [], onMenuOpen }, ref) => {
    const theme = useTheme();
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      setMenuAnchor(event.currentTarget);
      onMenuOpen?.();
    };

    const handleMenuClose = () => {
      setMenuAnchor(null);
    };

    const handleMenuItemClick = (onClick: () => void) => {
      onClick();
      handleMenuClose();
    };

    return (
      <TopBarContainer ref={ref}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            px: 3,
          }}
        >
          {/* Left section */}
          {
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {left}
            </Box>
          }

          {/* Right section + Menu */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {right}

            {/* Three dots menu - always present if items provided */}
            {menuItems.length > 0 && (
              <>
                <IconButton
                  aria-label="more options"
                  aria-controls={menuOpen ? 'top-bar-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuOpen ? 'true' : undefined}
                  onClick={handleMenuOpen}
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                >
                  <MoreVertIcon />
                </IconButton>

                <Menu
                  id="top-bar-menu"
                  anchorEl={menuAnchor}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }} slotProps={{ list: {
                    'aria-labelledby': 'top-bar-menu-button',
                  } }}
                >
                  {menuItems.map((item, index) => {
                    const menuItem = (
                      <MenuItem
                        onClick={() => handleMenuItemClick(item.onClick)}
                        disabled={item.disabled}
                      >
                        {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                        <ListItemText>{item.label}</ListItemText>
                        {item.shortcut && (
                          <Box
                            component="span"
                            sx={{
                              ml: 2,
                              color: 'text.secondary',
                              fontSize: '0.875rem',
                            }}
                          >
                            {item.shortcut}
                          </Box>
                        )}
                      </MenuItem>
                    );
                    return (
                      <React.Fragment key={index}>
                        {item.divider && <Divider />}
                        {item.tooltip ? (
                          <Tooltip title={item.tooltip} placement="left">
                            <span style={{ display: 'block' }}>{menuItem}</span>
                          </Tooltip>
                        ) : (
                          menuItem
                        )}
                      </React.Fragment>
                    );
                  })}
                </Menu>
              </>
            )}
          </Box>
        </Box>
      </TopBarContainer>
    );
  }
);

TopBarLayout.displayName = 'TopBarLayout';
